import type { GamePhase } from '../store/useGameStore';

export type MatchMode = 'live' | 'video';
export type CycleAction = 'Bloc' | 'Défense' | 'Passe' | 'Attaque';

const CYCLE: CycleAction[] = ['Bloc', 'Défense', 'Passe', 'Attaque'];

export const ACTION_TO_PHASE: Record<CycleAction, GamePhase> = {
  'Bloc': 'Block',
  'Défense': 'Dig',
  'Passe': 'Set',
  'Attaque': 'Attack',
};

const AFTER_ACTION_TARGET: Record<string, CycleAction> = {
  'Service': 'Bloc',
  'Réception': 'Passe',
  'Freeball Reçue': 'Passe',
  'Bloc': 'Défense',
  'Défense': 'Passe',
  'Passe': 'Attaque',
  'Attaque': 'Bloc',
  'Freeball Donnée': 'Bloc',
  'Retour Direct': 'Bloc',
};

export const ACTION_EQUIVALENCES: Record<string, CycleAction> = {
  'Freeball Donnée': 'Attaque',
  'Retour Direct': 'Attaque',
  'Freeball Reçue': 'Défense',
};

export const DEFAULT_LIVE_ACTIONS: CycleAction[] = ['Attaque'];
export const ALL_CYCLE_ACTIONS: CycleAction[] = [...CYCLE];

const CYCLE_ACTION_SET = new Set<string>(ALL_CYCLE_ACTIONS);

/**
 * « Attaque » est le pivot du cycle : sans elle, aucun rallye ne peut se terminer
 * autrement que par une faute. On la réinjecte donc toujours, quoi qu'ait coché
 * l'utilisateur au paramétrage.
 *
 * Source unique : utilisée par le store (`setEnabledActions`) ET par le payload de
 * création du match, pour que la valeur envoyée au backend soit exactement celle
 * que la saisie utilisera.
 */
export function normalizeEnabledActions(actions: readonly CycleAction[]): CycleAction[] {
  return actions.includes('Attaque') ? [...actions] : ['Attaque', ...actions];
}

export function filterCycleActions(actions: readonly string[]): CycleAction[] {
  return actions.filter((a): a is CycleAction => CYCLE_ACTION_SET.has(a));
}

export function findNextEnabledInCycle(
  target: CycleAction,
  enabledActions: Set<string>
): { action: CycleAction; phase: GamePhase } {
  const startIndex = CYCLE.indexOf(target);
  for (let i = 0; i < CYCLE.length; i++) {
    const nextIndex = (startIndex + i) % CYCLE.length;
    const candidate = CYCLE[nextIndex];
    if (enabledActions.has(candidate)) {
      return { action: candidate, phase: ACTION_TO_PHASE[candidate] };
    }
  }
  return { action: 'Attaque', phase: 'Attack' };
}

export function getNextTransition(
  currentAction: string,
  enabledActions: Set<string>
): { action: CycleAction; phase: GamePhase } {
  const target = AFTER_ACTION_TARGET[currentAction];
  if (!target) {
    return findNextEnabledInCycle('Bloc', enabledActions);
  }
  return findNextEnabledInCycle(target, enabledActions);
}

/**
 * Vrai quand l'action qui suit naturellement `currentAction` dans le cycle est
 * **désactivée** : la saisie « saute » alors une touche (ex. Réception → Passe
 * non statée → Attaque). Le joueur de l'action précédente redevient jouable,
 * puisqu'un autre joueur a touché le ballon entre les deux.
 */
export function isTransitionSkipped(
  currentAction: string,
  enabledActions: Set<string>
): boolean {
  const target = AFTER_ACTION_TARGET[currentAction];
  if (!target) return false;
  return !enabledActions.has(target);
}

export function isEquivalentEnabled(
  action: string,
  enabledActions: Set<string>
): boolean {
  const equivalent = ACTION_EQUIVALENCES[action];
  if (equivalent) {
    return enabledActions.has(equivalent);
  }
  return true;
}

export function getPossibleNextActions(
  currentAction: string,
  enabledActions: Set<string>
): CycleAction[] {
  const target = AFTER_ACTION_TARGET[currentAction];
  if (!target) return CYCLE.filter(a => enabledActions.has(a));
  
  const startIndex = CYCLE.indexOf(target);
  const result: CycleAction[] = [];
  
  if (enabledActions.has(target)) {
    result.push(target);
  }
  
  for (let i = 1; i < CYCLE.length; i++) {
    const nextIndex = (startIndex + i) % CYCLE.length;
    const candidate = CYCLE[nextIndex];
    if (enabledActions.has(candidate)) {
      result.push(candidate);
    }
  }
  
  return result;
}

export function buildEnabledActionsSet(actions: readonly CycleAction[]): Set<string> {
  return new Set<string>(actions);
}

const ENTRY_ACTIONS = new Set(['Service', 'Réception']);

export function filterActionsForLiveMode(
  actions: string[],
  enabledActions: Set<string>
): string[] {
  return actions.filter((action) => {
    if (ENTRY_ACTIONS.has(action)) return true;

    const equivalent = ACTION_EQUIVALENCES[action];
    if (equivalent) {
      return enabledActions.has(equivalent);
    }

    if ((ALL_CYCLE_ACTIONS as string[]).includes(action)) {
      return enabledActions.has(action);
    }

    return true;
  });
}
